// client/src/components/Login.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Container,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import LinkIcon from '@mui/icons-material/Link';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton, 
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Divider
} from '@mui/material';
import { getServerSettings, setServerSettings, APP_VERSION } from '../config';
import axios from 'axios';

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConnPassword, setShowConnPassword] = useState(false);
  
  // 2FA State
  const [requireTFA, setRequireTFA] = useState(false);
  const [tfaData, setTfaData] = useState({ userId: null, email_hint: '' });

  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();

  // Connection Settings State
  const [showConnDialog, setShowConnDialog] = useState(false);
  const [connSettings, setConnSettings] = useState(() => getServerSettings());
  const [connPassword, setConnPassword] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [connSuccess, setConnSuccess] = useState('');

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    try {
      const testUrl = `${connSettings.protocol}://${connSettings.ip}:${connSettings.port}/api/health`;
      const config = {};
      if (connSettings.username && connPassword) {
        config.headers = {
          'Authorization': `Basic ${btoa(`${connSettings.username}:${connPassword}`)}`
        };
      }
      const response = await axios.get(testUrl, config);
      if (response.status === 200) {
        setTestResult({ success: true, message: 'Connection successful!' });
      } else {
        setTestResult({ success: false, message: `Status: ${response.status}` });
      }
    } catch (err) {
      setTestResult({ success: false, message: `Failed: ${err.response?.data?.error || err.message}` });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveConnection = () => {
    setServerSettings({ ...connSettings, password: connPassword || undefined });
    setConnSuccess('Settings saved. Reloading...');
    setTimeout(() => window.location.reload(), 1500);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) return;
    
    setLoading(true);
    setError('');

    try {
      const result = await login(identifier, password);
      if (result.success) {
        if (result.require_2fa) {
          setRequireTFA(true);
          setTfaData({ userId: result.userId, email_hint: result.email_hint });
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    if (!otpCode) return;
    
    setLoading(true);
    setError('');

    try {
      const result = await verify2FA(tfaData.userId, otpCode);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={0} 
        sx={{ 
          mt: 12, 
          p: 5, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
        }}
      >
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mb: -2, mt: -2 }}>
          <Tooltip title="Server Connection Settings">
            <IconButton onClick={() => setShowConnDialog(true)} color="primary" size="small">
              <LinkIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box 
          sx={{ 
            mb: 3, 
            p: 1.5, 
            borderRadius: '50%', 
            bgcolor: requireTFA ? 'warning.light' : 'primary.light',
            color: requireTFA ? 'warning.main' : 'primary.main',
            display: 'flex'
          }}
        >
          {requireTFA ? <SecurityIcon fontSize="large" /> : <LockOutlinedIcon fontSize="large" />}
        </Box>

        <Typography component="h1" variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          {requireTFA ? 'Security Check' : 'MyEMR Gateway'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {requireTFA 
            ? `Please enter the 6-digit code sent to ${tfaData.email_hint}`
            : 'Enter your credentials to access the clinical system'
          }
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        
        {!requireTFA ? (
          <Box component="form" onSubmit={handleLoginSubmit} noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="identifier"
              label="Email or Username"
              name="identifier"
              autoComplete="username"
              autoFocus
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ 
                mt: 4, 
                mb: 2, 
                py: 1.6, 
                borderRadius: 2.5, 
                textTransform: 'none', 
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: 'none'
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Log In to System'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleOTPSubmit} noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="otp"
              label="Verification Code"
              name="otp"
              placeholder="000000"
              autoFocus
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
              inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '8px', fontSize: '1.5rem' } }}
              disabled={loading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="warning"
              size="large"
              disabled={loading || otpCode.length !== 6}
              sx={{ 
                mt: 4, 
                mb: 2, 
                py: 1.6, 
                borderRadius: 2.5, 
                textTransform: 'none', 
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: 'none'
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Sign In'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => setRequireTFA(false)}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Back to Login
            </Button>
          </Box>
        )}
      </Paper>
      
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
        MyEMR v{APP_VERSION} Clinical Administration System by Ioannis Bolanis 2026
      </Typography>

      {/* Connection Settings Dialog */}
      <Dialog 
        open={showConnDialog} 
        onClose={() => !testingConnection && setShowConnDialog(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Server Connection Settings
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure where the client should connect. Useful for multi-workstation setups.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Protocol</InputLabel>
                <Select
                  value={connSettings.protocol}
                  label="Protocol"
                  onChange={(e) => setConnSettings(prev => ({ ...prev, protocol: e.target.value }))}
                >
                  <MenuItem value="http">HTTP</MenuItem>
                  <MenuItem value="https">HTTPS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Server IP / Hostname"
                value={connSettings.ip}
                onChange={(e) => setConnSettings(prev => ({ ...prev, ip: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Port"
                type="number"
                value={connSettings.port}
                onChange={(e) => setConnSettings(prev => ({ ...prev, port: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                OPTIONAL CREDENTIALS
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Connection Username"
                value={connSettings.username}
                onChange={(e) => setConnSettings(prev => ({ ...prev, username: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type={showConnPassword ? 'text' : 'password'}
                label="Connection Password"
                value={connPassword}
                onChange={(e) => setConnPassword(e.target.value)}
                placeholder={connSettings.hasPassword ? "••••••••" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConnPassword(!showConnPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConnPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              {testResult && (
                <Alert severity={testResult.success ? "success" : "error"} sx={{ mt: 1, borderRadius: 2 }}>
                  {testResult.message}
                </Alert>
              )}
              {connSuccess && (
                <Alert severity="success" sx={{ mt: 1, borderRadius: 2 }}>
                  {connSuccess}
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleTestConnection} 
            disabled={testingConnection || !connSettings.ip}
            startIcon={testingConnection ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            Test
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setShowConnDialog(false)} color="inherit">Cancel</Button>
          <Button 
            onClick={handleSaveConnection} 
            variant="contained" 
            disabled={!connSettings.ip || !!connSuccess}
          >
            Save & Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;